/**
 * Notification Preference Schemas
 *
 * Types and schemas for per-user notification preferences.
 * Includes admin types (weekly_summary, new_user_signup) and system notification types
 * (new_user, new_content, task_failure) for GetStream-based admin notifications,
 * and the chat unread email (chat_unread_email, SCH-2312), offered to anyone chat
 * is on for at their school (SCH-2430).
 */

import { z } from 'zod'

// --- Notification Categories ---

export const NOTIFICATION_CATEGORIES = ['admin', 'assignments', 'chat', 'content', 'system'] as const
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number]

// --- Notification Types ---

export const NOTIFICATION_TYPES = [
  'admin_weekly_summary',
  'admin_new_user_signup',
  'admin_system_new_user',
  'admin_system_new_content',
  'admin_system_task_failure',
  'chat_unread_email'
] as const
export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

// --- Notification Channels ---

export const NOTIFICATION_CHANNELS = ['email', 'push', 'sms', 'web'] as const
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]

// --- Type-to-Category Mapping ---

export const NOTIFICATION_TYPE_CATEGORY: Record<NotificationType, NotificationCategory> = {
  admin_weekly_summary: 'admin',
  admin_new_user_signup: 'admin',
  admin_system_new_user: 'system',
  admin_system_new_content: 'system',
  admin_system_task_failure: 'system',
  chat_unread_email: 'chat'
}

// --- Role Visibility ---

/**
 * Which notification types are visible to which roles. `chat_unread_email` lists
 * none: it is not role-gated. The server returns it only to someone chat is on
 * for at their school (SCH-2430), and clients show it exactly when it is returned.
 */
export const NOTIFICATION_TYPE_ROLES: Record<NotificationType, ('admin' | 'parent' | 'student' | 'faculty')[]> = {
  admin_weekly_summary: ['admin'],
  admin_new_user_signup: ['admin'],
  admin_system_new_user: ['admin'],
  admin_system_new_content: ['admin'],
  admin_system_task_failure: ['admin'],
  chat_unread_email: []
}

// --- Zod Schemas ---

export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES)
export const NotificationTypeSchema = z.enum(NOTIFICATION_TYPES)
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS)

/** Schema for channel preferences on a single notification type */
export const NotificationChannelsSchema = z.object({
  email: z.boolean(),
  push: z.boolean(),
  sms: z.boolean(),
  web: z.boolean()
})

export type NotificationChannels = z.infer<typeof NotificationChannelsSchema>

// --- Preference Source ---

/**
 * Who supplied a preference item's effective value.
 *
 * - `self`: the user's own row (a row with no recorded setter counts as the user's own).
 * - `admin`: a school admin set it for this one person.
 * - `none`: no row; the value is the type's built-in default.
 */
export const NOTIFICATION_PREFERENCE_SOURCES = ['self', 'admin', 'none'] as const
export type NotificationPreferenceSource = (typeof NOTIFICATION_PREFERENCE_SOURCES)[number]
export const NotificationPreferenceSourceSchema = z.enum(NOTIFICATION_PREFERENCE_SOURCES)

/** Schema for a single preference item in API responses */
export const NotificationPreferenceItemSchema = z.object({
  type: NotificationTypeSchema,
  category: NotificationCategorySchema,
  email: z.boolean(),
  push: z.boolean(),
  sms: z.boolean(),
  web: z.boolean(),
  isDefault: z.boolean(),
  source: NotificationPreferenceSourceSchema
})

export type NotificationPreferenceItem = z.infer<typeof NotificationPreferenceItemSchema>

/** Schema for the update request body */
export const UpdateNotificationPreferenceBodySchema = z.object({
  email: z.boolean().optional(),
  push: z.boolean().optional(),
  sms: z.boolean().optional(),
  web: z.boolean().optional()
})

export type UpdateNotificationPreferenceBody = z.infer<typeof UpdateNotificationPreferenceBodySchema>

// --- Notification Dispatch API ---

/** Schema for notification dispatch request */
export const NotificationDispatchRequestSchema = z.object({
  notification_type: NotificationTypeSchema,
  school_id: z.string().optional(),
  user_id: z.string().optional(),
  dryRun: z.boolean().optional().default(false)
})

export type NotificationDispatchRequest = z.infer<typeof NotificationDispatchRequestSchema>

/** Schema for notification dispatch response */
export const NotificationDispatchResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    notificationType: z.string(),
    schoolsProcessed: z.number(),
    totalSent: z.number(),
    totalSkipped: z.number(),
    totalFailed: z.number(),
    details: z
      .array(
        z.object({
          schoolId: z.string(),
          schoolName: z.string().nullable(),
          sent: z.number(),
          skipped: z.number(),
          failed: z.number()
        })
      )
      .optional()
  })
})

export type NotificationDispatchResponse = z.infer<typeof NotificationDispatchResponseSchema>

// --- System Feed Helpers (reserved for future school-wide announcement feeds) ---
// NOTE: Admin notifications use direct delivery to individual notification feeds.
// These helpers are for potential school-wide system feed features.

/** Feed group name in GetStream for system notifications */
export const SYSTEM_FEED_GROUP = 'system'

/** Feed ID for a school's admin notification feed */
export function getSchoolAdminFeedId(schoolPublicId: string): string {
  return `school_admin_${schoolPublicId}`
}

/** Feed ID for a school's user notification feed (all users) */
export function getSchoolUserFeedId(schoolPublicId: string): string {
  return `school_user_${schoolPublicId}`
}

/** Feed ID for super admin notifications */
export const SUPER_ADMIN_FEED_ID = 'super_admin'
